import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import useGetMyAvailableTags from '@/api/queries/me/useGetMyAvailableTags';
import { useRegion } from '@/api/rest/users/useRegion';
import useGetPipelineStage from '@/api/queries/pipelines/useGetPipelineStage';
import {
  AutomationConditionOperator,
  AutomationConditionType,
  AutomationEntityType,
  AutomationTriggerEvent,
} from '@/gql/graphql';
import { triggerEventToGqlEnum } from '@/helpers/automation';
import { useTranslation } from '@/i18n';
import useDraftCreateAutomation from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftCreateAutomation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Form, Input, Modal, Select } from '@payaca/ambrosia';
import TagSelectCombobox from '@payaca/components/tagSelectCombobox/TagSelectCombobox';
import {
  conditionTypeOperators,
  getAvailableConditionTypes,
} from '@payaca/gql-helpers';
import { automationEntityTypeTriggerEventMap } from '@payaca/helpers/automationHelper';
import { conditionTypeNames as CONDITION_TYPE_NAMES } from '@payaca/types/automationSchema';
import { AccountRegions } from '@payaca/types/accountTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { z } from 'zod/v4';
import {
  buildConditionValueInput,
  CONDITION_TYPE_VALUE_KIND,
  EXCLUDED_CONDITION_TYPES,
  getStaticValueOptions,
} from './conditionConfig';

// Entity types available for pipeline automations (excluding legacy Estimate/Quote)
const PIPELINE_ENTITY_TYPES: AutomationEntityType[] = [
  'PROJECT',
  'INVOICE',
  'PROPOSAL',
  'CHANGE_PROPOSAL',
  'EVENT',
  'DNO_APPLICATION',
];

// Mapping of entity types to their valid trigger events (using GraphQL enum values)
const ENTITY_TYPE_TRIGGER_EVENTS: Record<
  AutomationEntityType,
  AutomationTriggerEvent[]
> = {
  PROJECT: (automationEntityTypeTriggerEventMap['Deal'] ?? []).map(
    (event) => triggerEventToGqlEnum[event]
  ),
  INVOICE: (automationEntityTypeTriggerEventMap['Invoice'] ?? []).map(
    (event) => triggerEventToGqlEnum[event]
  ),
  PROPOSAL: (automationEntityTypeTriggerEventMap['Proposal'] ?? []).map(
    (event) => triggerEventToGqlEnum[event]
  ),
  CHANGE_PROPOSAL: (
    automationEntityTypeTriggerEventMap['Change Proposal'] ?? []
  ).map((event) => triggerEventToGqlEnum[event]),
  EVENT: (automationEntityTypeTriggerEventMap['Event'] ?? []).map(
    (event) => triggerEventToGqlEnum[event]
  ),
  DNO_APPLICATION: (
    automationEntityTypeTriggerEventMap['DNO Application'] ?? []
  ).map((event) => triggerEventToGqlEnum[event]),
  // Types not surfaced in the pipeline automation modal but needed for type completeness
  ESTIMATE: [],
  QUOTE: [],
  DOCUMENT_INSTANCE: [],
};

const DEFAULT_OPERATORS: AutomationConditionOperator[] = ['EQUALS'];

const OPERATORS = ['EQUALS', 'INCLUDES', 'NOT_EQUALS'] as const;

const conditionRowSchema = z.object({
  type: z.enum(CONDITION_TYPE_NAMES),
  operator: z.enum(OPERATORS),
  value: z.string().min(1, 'Value is required'),
});

const formSchema = z.object({
  conditions: z.array(conditionRowSchema),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  pipelinePublicId?: string;
  pipelineStagePublicId?: string;
  pipelineTitle?: string;
  isOpen: boolean;
  onClose: () => void;
}

function PipelineAutomationTriggerModal(props: Props) {
  const {
    pipelinePublicId,
    pipelineStagePublicId,
    pipelineTitle,
    isOpen,
    onClose,
  } = props;
  const t = useTranslation('automations');
  const region = useRegion();

  const { pipelineStage } = useGetPipelineStage({
    pipelinePublicId,
    pipelineStagePublicId,
  });

  const [selectedEntityType, setSelectedEntityType] =
    useState<AutomationEntityType>(PIPELINE_ENTITY_TYPES[0]);

  const triggerEventOptions = useMemo(() => {
    const triggerEvents = ENTITY_TYPE_TRIGGER_EVENTS[selectedEntityType];
    return triggerEvents.map((event) => ({
      label: t(`triggerEvents.${event}`, { entity: '' }).trim(),
      value: event,
    }));
  }, [selectedEntityType, t]);

  const [selectedTriggerEvent, setSelectedTriggerEvent] =
    useState<AutomationTriggerEvent>(triggerEventOptions[0]?.value);

  // Reset trigger event when entity type changes
  useEffect(() => {
    setSelectedTriggerEvent(triggerEventOptions[0]?.value);
  }, [triggerEventOptions]);

  // Conditions form
  const formMethods = useForm<FormValues>({
    defaultValues: { conditions: [] },
    resolver: zodResolver(formSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'conditions',
  });

  // Reset conditions when entity type or trigger event changes
  useEffect(() => {
    formMethods.reset({ conditions: [] });
  }, [selectedEntityType, selectedTriggerEvent, formMethods]);

  // Reset modal state when closed (modal stays mounted for close animation)
  useEffect(() => {
    if (!isOpen) {
      setSelectedEntityType(PIPELINE_ENTITY_TYPES[0]);
      formMethods.reset({ conditions: [] });
    }
  }, [isOpen, formMethods]);

  const conditionTypeOptions = useMemo(
    () =>
      getAvailableConditionTypes(selectedEntityType, selectedTriggerEvent)
        .filter((type) => !EXCLUDED_CONDITION_TYPES.includes(type))
        .map((type) => ({
          label: t(`conditions.typeNames.${type}`),
          value: type,
        })),
    [selectedEntityType, selectedTriggerEvent, t]
  );

  // Dynamic options for condition value dropdowns
  const { data: pipelines } = useGetMyAccountPipelines();
  const { data: tags } = useGetMyAvailableTags({ status: ['active'] });

  const dynamicValueOptions = useMemo(() => {
    const pipelineOptions = (pipelines ?? []).map((p) => ({
      label: p.title,
      value: p.publicId,
    }));

    const stageNames = new Set(
      (pipelines ?? []).flatMap((p) => p.stages.map((s) => s.title))
    );
    const pipelineStageNameOptions = [...stageNames].map((name) => ({
      label: name,
      value: name,
    }));

    return {
      pipeline: pipelineOptions,
      pipelineStageName: pipelineStageNameOptions,
    };
  }, [pipelines]);

  const availableTags = useMemo(
    () =>
      (tags ?? []).map((tag) => ({
        id: tag.name,
        tagText: tag.name,
        tagColour: tag.colour ?? '',
      })),
    [tags]
  );

  const { mutate: createAutomation } = useDraftCreateAutomation({
    pipelinePublicId: pipelinePublicId ?? '',
    pipelineStagePublicId: pipelineStagePublicId ?? '',
    pipelineTitle,
    stageTitle: pipelineStage?.title,
  });

  const handleSubmit = (data: FormValues) => {
    if (
      !pipelinePublicId ||
      !pipelineStagePublicId ||
      !pipelineStage ||
      (selectedEntityType === 'DNO_APPLICATION' &&
        region !== AccountRegions.UK) ||
      !selectedEntityType ||
      !selectedTriggerEvent
    ) {
      return;
    }

    const conditionsInput = data.conditions.map((c) => {
      const valueInput = buildConditionValueInput(c.type, c.value);
      if (!valueInput) {
        throw new Error(`Invalid condition value for type ${c.type}`);
      }
      return { type: c.type, operator: c.operator, value: valueInput };
    });

    createAutomation({
      entityType: selectedEntityType,
      triggerEvent: selectedTriggerEvent,
      pipelineStageId: pipelineStagePublicId,
      ...(conditionsInput.length > 0 ? { conditions: conditionsInput } : {}),
    });

    onClose();
  };

  return (
    <Modal size="lg" isOpen={isOpen} title="Choose trigger" onClose={onClose}>
      <Form methods={formMethods} onSubmit={handleSubmit}>
        <Modal.Body>
          <p>
            Projects move to &lsquo;{pipelineStage?.title}&rsquo; pipeline stage
            when:
          </p>

          <div className="mt-4 flex flex-col gap-4 md:flex-row">
            <Select
              multiple={false}
              onChange={(value) => {
                setSelectedEntityType(value);
              }}
              options={PIPELINE_ENTITY_TYPES.filter(
                (entityType) =>
                  entityType !== 'DNO_APPLICATION' ||
                  region === AccountRegions.UK
              ).map((entityType) => ({
                label: t(`entityTypes.${entityType}`),
                value: entityType,
              }))}
              value={selectedEntityType}
              placeholder="Select automation type"
            />
            <Select
              multiple={false}
              onChange={(value) => {
                setSelectedTriggerEvent(value);
              }}
              options={triggerEventOptions}
              value={selectedTriggerEvent}
              placeholder="Select trigger event"
              disabled={!selectedEntityType}
            />
          </div>

          {conditionTypeOptions.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <p>Conditions</p>
              </div>
              {fields.length === 0 && (
                <p className="supporting-body text-gray-500 mb-2">
                  No conditions added. The automation will fire for all{' '}
                  {t(`entityTypes.${selectedEntityType}`).toLowerCase()}s
                  matching the trigger.
                </p>
              )}
              <div className="flex flex-col gap-3">
                {fields.map((field, index) => (
                  <ConditionRowInputs
                    key={field.id}
                    index={index}
                    conditionTypeOptions={conditionTypeOptions}
                    dynamicValueOptions={dynamicValueOptions}
                    availableTags={availableTags}
                    onRemove={() => remove(index)}
                  />
                ))}
              </div>
              <Button
                className="mt-3"
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => {
                  const firstType = conditionTypeOptions[0].value;
                  append({
                    type: firstType,
                    operator: conditionTypeOperators[firstType][0],
                    value: '',
                  });
                }}
              >
                Add condition
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button onClick={onClose} variant="secondary" type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={!selectedTriggerEvent}>
              Add
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default PipelineAutomationTriggerModal;

// ---

type AvailableTag = { id: string; tagText: string; tagColour: string };

type ConditionRowInputsProps = {
  index: number;
  conditionTypeOptions: { label: string; value: AutomationConditionType }[];
  dynamicValueOptions: Record<string, { label: string; value: string }[]>;
  availableTags: AvailableTag[];
  onRemove: () => void;
};

function ConditionRowInputs(props: ConditionRowInputsProps) {
  const {
    index,
    conditionTypeOptions,
    dynamicValueOptions,
    availableTags,
    onRemove,
  } = props;
  const t = useTranslation('automations');
  const { setValue, watch } = useFormContext<FormValues>();

  const conditionType = watch(`conditions.${index}.type`);

  const valueKind = conditionType
    ? CONDITION_TYPE_VALUE_KIND[conditionType]
    : null;

  const valueOptions =
    valueKind && valueKind !== 'tag'
      ? (getStaticValueOptions(valueKind, t) ??
        dynamicValueOptions[valueKind] ??
        null)
      : null;

  const allowedOperators = useMemo(() => {
    if (!conditionType) return DEFAULT_OPERATORS;
    const operators = conditionTypeOperators[conditionType];
    if (valueKind === 'tag') return operators.filter((op) => op !== 'INCLUDES');
    return operators;
  }, [conditionType, valueKind]);

  return (
    <div className="flex items-center gap-2">
      {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values */}
      <div className="grid grid-cols-1 gap-2 grow min-w-0 md:grid-cols-[2fr_1fr_2fr]">
        <Controller<FormValues, `conditions.${number}.type`>
          name={`conditions.${index}.type`}
          render={({ field: { name, value, onChange } }) => (
            <Field name={name}>
              <Select
                multiple={false}
                options={conditionTypeOptions}
                value={value || undefined}
                onChange={(newType) => {
                  onChange(newType);
                  setValue(`conditions.${index}.value`, '');
                  setValue(
                    `conditions.${index}.operator`,
                    conditionTypeOperators[newType][0]
                  );
                }}
                placeholder="Select condition"
              />
            </Field>
          )}
        />
        <Controller<FormValues, `conditions.${number}.operator`>
          name={`conditions.${index}.operator`}
          render={({ field: { value, onChange } }) => (
            <Select
              multiple={false}
              options={allowedOperators.map((op) => ({
                label: t(`conditions.operators.${op}`),
                value: op,
              }))}
              value={value}
              onChange={onChange}
              disabled={!conditionType}
            />
          )}
        />
        <Controller<FormValues, `conditions.${number}.value`>
          name={`conditions.${index}.value`}
          render={({ field: { name, value, onChange } }) => (
            <Field name={name}>
              <ConditionValueInput
                valueKind={valueKind}
                availableTags={availableTags}
                value={value}
                valueOptions={valueOptions}
                disabled={!conditionType}
                onChange={onChange}
              />
            </Field>
          )}
        />
      </div>
      <Button variant="quaternary" size="xs" type="button" onClick={onRemove}>
        <span className="sr-only">Remove condition</span>
        <UntitledIcon className="h-3 w-3" name="x-close" />
      </Button>
    </div>
  );
}

type ConditionValueInputProps = {
  valueKind: string | null;
  availableTags: AvailableTag[];
  value: string;
  valueOptions: { label: string; value: string }[] | null;
  disabled: boolean;
  onChange: (value: string) => void;
};

function ConditionValueInput(props: ConditionValueInputProps) {
  const { valueKind, availableTags, value, valueOptions, disabled, onChange } =
    props;

  if (valueKind === 'tag') {
    return (
      <TagSelectCombobox
        multiple={false}
        availableTags={availableTags}
        value={value || undefined}
        onChange={onChange}
        placeholder="Select tag"
      />
    );
  }
  if (valueOptions) {
    return (
      <Select
        multiple={false}
        options={valueOptions}
        value={value || undefined}
        onChange={onChange}
        placeholder="Select value"
      />
    );
  }
  return (
    <Input
      value={value}
      onChange={onChange}
      placeholder="Enter value"
      disabled={disabled}
    />
  );
}
